type TimerId = ReturnType<typeof setTimeout>;

export interface CoalescedRefresherOptions {
  /** 真正去重抓的動作；reject 會被吞掉（重抓是靜默的） */
  run: () => Promise<void> | void;
  /** 最後一次 request 之後安靜多久才跑 */
  delayMs: number;
  /** 從第一次 request 起算，最多等多久就一定要跑 */
  maxWaitMs: number;
  setTimer?: (fn: () => void, ms: number) => TimerId;
  clearTimer?: (id: TimerId) => void;
  now?: () => number;
}

/**
 * 把一連串「該重抓了」的要求合併成少數幾次真正的重抓。
 *
 * - trailing debounce：mirror 同一批同步可能連發好幾個事件，等安靜下來再一次抓完。
 * - maxWait：事件一直來時也不能一直往後延，畫面最慢 maxWaitMs 就要更新一次。
 * - 執行中又有要求：不能疊打（兩個回應先後不定，舊的可能蓋掉新的），
 *   但也不能丟——要求可能是在這次請求送出「之後」才發生的變化，所以跑完再補一次；
 *   中途要求再多次也只補一次，補的那次自然會拿到最新狀態。
 */
export function createCoalescedRefresher({
  run,
  delayMs,
  maxWaitMs,
  setTimer = (fn: () => void, ms: number) => setTimeout(fn, ms),
  clearTimer = (id: TimerId) => clearTimeout(id),
  now = Date.now,
}: CoalescedRefresherOptions): { request(): void; cancel(): void } {
  let timer: TimerId | null = null;
  /** 這一輪第一次 request 的時間；跑完歸零 */
  let firstRequestAt: number | null = null;
  let running = false;
  let pending = false;
  let canceled = false;

  const schedule = () => {
    const t = now();
    if (firstRequestAt === null) firstRequestAt = t;
    if (timer !== null) clearTimer(timer);
    const wait = Math.max(0, Math.min(delayMs, firstRequestAt + maxWaitMs - t));
    timer = setTimer(fire, wait);
  };

  function fire() {
    timer = null;
    firstRequestAt = null;
    if (canceled) return;
    running = true;
    // 同步呼叫 run（而不是排進 microtask），計時器到期那一刻請求就送出；同步 throw 也收進 promise 一起吞
    let result: Promise<void> | void;
    try {
      result = run();
    } catch (error) {
      result = Promise.reject(error);
    }
    Promise.resolve(result)
      .catch(() => {
        // 靜默重抓：失敗就維持畫面現狀，下一個事件或輪詢會再試
      })
      .then(() => {
        running = false;
        if (canceled || !pending) return;
        pending = false;
        schedule();
      });
  }

  return {
    request() {
      if (canceled) return;
      if (running) {
        pending = true;
        return;
      }
      schedule();
    },
    cancel() {
      canceled = true;
      pending = false;
      if (timer !== null) clearTimer(timer);
      timer = null;
    },
  };
}
